import NoPermissionsTooltipWrapper from "@src/lib/ui/NoPermissionsTooltipWrapper";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import { usePrevious } from "@src/lib/hooks/usePrevious";
import { cn } from "@src/lib/utils/cn";
import React, { FC, forwardRef, useCallback } from "react";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import XIcon from "@src/lib/icons/18px/x";
import { Separator } from "@src/lib/ui/Separator";

export type MultiSelectAction = {
  icon?: React.ReactElement;
  key: string;
  label?: string;
  hasPermission?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  component?: React.ReactNode;
};

export type MultiSelectOnDestroy = undefined | (() => void);
export type Props = {
  numSelected: number;
  onDeselectAll: () => void;
  onDestroy?: MultiSelectOnDestroy;
  destroyLabel?: string;
  hasDestroyPermission?: boolean;
  actions?: MultiSelectAction[];
  hidden?: boolean;
  selectAll?: () => void;
  selectAllLoading?: boolean;
  totalItemCount?: number;
};

const MultiSelectBanner = forwardRef<HTMLDivElement, Props>(
  (
    {
      numSelected,
      onDeselectAll,
      actions,
      onDestroy,
      hasDestroyPermission = true,
      hidden,
      selectAll,
      totalItemCount,
      selectAllLoading,
      destroyLabel = "Remove",
    },
    ref,
  ) => {
    const previousSelectAllLoading = usePrevious(selectAllLoading);
    if (numSelected === 0 || hidden) {
      return null;
    }
    const selectAllLoadingComplete =
      previousSelectAllLoading && !selectAllLoading;
    return (
      <div
        className="fixed inset-x-0 bottom-8 z-[48] mx-auto max-w-[60%] rounded-lg border border-gray-5-alpha bg-white shadow-lg"
        ref={ref}
      >
        <div className="flex items-center p-3">
          <XIcon onClick={onDeselectAll} className="mr-2 cursor-pointer" />
          <Text variant="compact-title">{numSelected} selected</Text>
          <div className="ml-4 flex items-center max-[1340px]:hidden">
            <Text variant="fine-print-description" className="text-gray-7">
              <Text variant="fine-print-title" className="text-gray-7">
                Pro tip:&nbsp;
              </Text>
              Hold the
            </Text>
            <div className="mx-1 flex items-center rounded border border-gray-5-alpha px-1 py-0.5">
              <Text variant="fine-print-description" className="text-gray-7">
                shift
              </Text>
            </div>
            <Text variant="fine-print-description" className="text-gray-7">
              key to select multiple rows with 1 click
            </Text>
          </div>
          {selectAll &&
            numSelected !== totalItemCount &&
            !selectAllLoadingComplete && (
              <div className="ml-auto flex cursor-pointer items-center">
                {selectAllLoading ? (
                  <Spinner size={16} style={{ marginRight: 8 }} />
                ) : (
                  <div
                    onClick={selectAll}
                    className="mr-1 flex cursor-pointer select-none items-center rounded-lg px-3 py-1 hover:bg-gray-5-alpha"
                  >
                    <Text variant="compact-title">
                      Select all {totalItemCount}
                    </Text>
                  </div>
                )}
              </div>
            )}
        </div>
        <Separator />
        <div className="flex items-center overflow-x-auto p-2">
          {actions && (
            <div className="flex items-center gap-3">
              {actions.length > 1 && (
                <div className="pl-1">
                  <Text variant="compact-description">Actions:</Text>
                </div>
              )}
              {actions.map(
                (action) =>
                  action.component || (
                    <MultiSelectBannerButton action={action} key={action.key} />
                  ),
              )}
            </div>
          )}
          {onDestroy ? (
            <NoPermissionsTooltipWrapper
              hasPermission={hasDestroyPermission}
              caretPosition="Top"
              tooltipSide="bottom"
            >
              <span
                className={cn(
                  "select-none flex items-center cursor-pointer mr-1 rounded-lg py-1 px-2 hover:bg-gray-5-alpha",
                  "ml-auto mr-0",
                  !hasDestroyPermission && "opacity-50 hover:bg-transparent",
                )}
                onClick={!hasDestroyPermission ? undefined : onDestroy}
              >
                <TrashOutlineIcon className="mr-1" />
                <Text variant="compact-title" inline>
                  {destroyLabel}
                </Text>
              </span>
            </NoPermissionsTooltipWrapper>
          ) : null}
        </div>
      </div>
    );
  },
);

MultiSelectBanner.displayName = "MultiSelectBanner";

export default MultiSelectBanner;

type MultiSelectBannerButtonProps = {
  action: MultiSelectAction;
};

export const MultiSelectBannerButton: FC<MultiSelectBannerButtonProps> = ({
  action,
}) => {
  const lacksPermission = action.hasPermission === false;
  const onClickCallback = useCallback(() => {
    if (action.disabled || lacksPermission) {
      return;
    }
    if (action.onClick) {
      action.onClick();
    }
  }, [action, lacksPermission]);
  return (
    <div
      key={action.key}
      className={cn(
        "select-none flex items-center cursor-pointer mr-1 rounded-lg py-1 px-2 hover:bg-gray-5-alpha",
        (action.disabled || lacksPermission) &&
          "opacity-50 hover:bg-transparent",
      )}
      onClick={onClickCallback}
    >
      {action.icon && (
        <span className="mr-1 flex items-center justify-center">
          {action.icon}
        </span>
      )}
      <Text variant="compact-title" inline>
        {action.label}
      </Text>
    </div>
  );
};
